import { useMemo } from 'react';
import { formatRecency, isValidTimestamp } from './format-recency';

export interface UseRecencyResult {
  formatted: string | null;
  isAvailable: boolean;
  ariaLabel: string;
}

/**
 * Hook for formatting and displaying listing recency.
 * 
 * Handles:
 * - Timestamp validation (not null, not in future)
 * - Formatting to relative time ("2 days ago")
 * - Accessibility label generation
 * 
 * @param timestamp - The listing's added date
 * @param locale - Locale for formatting (e.g., 'en-CA')
 */
export function useRecency(timestamp: Date | string | null, locale: string): UseRecencyResult {
  return useMemo(() => {
    // Normalize to Date
    const date = timestamp instanceof Date 
      ? timestamp 
      : timestamp 
        ? new Date(timestamp) 
        : null;
    
    if (!isValidTimestamp(date)) {
      return {
        formatted: null,
        isAvailable: false,
        ariaLabel: '',
      };
    }

    const formatted = formatRecency(date, locale);
    
    if (!formatted) {
      return {
        formatted: null,
        isAvailable: false,
        ariaLabel: '',
      };
    }

    return {
      formatted,
      isAvailable: true,
      ariaLabel: `Added ${formatted}`,
    };
  }, [timestamp, locale]);
}
